import { useEffect, useState } from 'react'

import { CustomFieldSchema } from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { getFieldIcon } from '@tribeplatform/react-components/CustomField'
import { FieldModal } from '@tribeplatform/react-components/CustomFields'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { PostCardListLoading } from '@tribeplatform/react-components/LoadingStates'
import {
  useReorderMemberSchemaFields,
  useAddMemberSchemaField,
  useArchiveMemberSchemaField,
  useAuthToken,
  useNetwork,
  useUnArchiveMemberSchemaField,
  useUpdateMemberSchemaField,
} from '@tribeplatform/react-sdk/hooks'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { SvgIcon, Icon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { SortableList } from '@tribeplatform/react-ui-kit/SortableList'

import { MemberAbout } from '../../Member/MemberAbout.js'
import { MemberHeader } from '../../Member/MemberHeader.js'
import { suggestedFields } from './suggestedFields.js'

export const FieldRow = ({
  field,
  setHighlightedField,
  setFieldModal,
  dragHandle = null,
}) => {
  const { $t } = useI18n()
  const { mutateAsync: archiveField } = useArchiveMemberSchemaField()
  const { mutateAsync: unArchiveField } = useUnArchiveMemberSchemaField()

  return (
    <div
      className="group w-full relative flex items-center"
      onMouseEnter={() => setHighlightedField(field.key)}
      onMouseLeave={() => setHighlightedField('')}
    >
      <button
        type="button"
        onClick={() => setFieldModal(field)}
        className="w-full flex-1 border border-line-subdued rounded-base p-2 px-1 bg-surface hover:bg-surface-hovered flex space-s-2 items-center"
      >
        {dragHandle}
        <Icon size="lg">{getFieldIcon(field)}</Icon>
        <span className="flex-grow text-start">{field.name}</span>
      </button>
      <div className="absolute end-1 inset-y-0 flex items-center">
        {field.archived ? (
          <>
            <IconButton
              onClick={async e => {
                e.preventDefault()
                e.stopPropagation()
                const confirmed = await confirm({
                  title: $t(
                    {
                      defaultMessage:
                        'Do you want to unarchive {field_name} ({field_key})?',
                      id: 'Admin.MemberFields.Unarchive.Confirmation',
                    },
                    {
                      field_name: field.name,
                      field_key: field.key,
                    },
                  ),
                  proceedLabel: $t({
                    defaultMessage: 'Unarchive',
                    id: 'Generics.Unarchive',
                  }),
                  cancelLabel: $t({
                    defaultMessage: 'Cancel',
                    id: 'Generics.Cancel',
                  }),
                })

                if (!confirmed) {
                  return
                }

                unArchiveField({ key: field.key })
              }}
              className="opacity-0 group-hover:opacity-100"
              variant="tertiaryNeutral"
              icon={<SvgIcon name="plus" />}
              size="sm"
              rounded
            />
            <div className="pe-1 block group-hover:hidden">
              <SvgIcon
                className="w-4 h-4 text-content-subdued"
                name="eye-off"
              />
            </div>
          </>
        ) : (
          <IconButton
            onClick={async e => {
              e.preventDefault()
              e.stopPropagation()
              const confirmed = await confirm({
                title: $t(
                  {
                    defaultMessage:
                      'Are you sure you want to archive {field_name} ({field_key})?',
                    id: 'Admin.MemberFields.Archive.Confirmation',
                  },
                  {
                    field_name: field.name,
                    field_key: field.key,
                  },
                ),
                description: $t({
                  defaultMessage:
                    'Archiving a field will not delete the data for the field, therefore, this action is reversible and you can always unarchive!',
                  id: 'Admin.MemberFields.Archive.Description',
                }),
                proceedLabel: $t({
                  defaultMessage: 'Archive',
                  id: 'Generics.Archive.Verb',
                }),
                cancelLabel: $t({
                  defaultMessage: 'Cancel',
                  id: 'Generics.Cancel',
                }),
                danger: true,
              })

              if (!confirmed) {
                return
              }

              archiveField({ key: field.key })
            }}
            className="opacity-0 group-hover:opacity-100"
            variant="tertiaryNeutral"
            icon={<SvgIcon name="close" />}
            size="sm"
            rounded
          />
        )}
      </div>
    </div>
  )
}

export const MemberFields = () => {
  const { $t } = useI18n()
  const {
    data: { member },
  } = useAuthToken()
  const {
    data: {
      memberFields: { fields },
    },
  } = useNetwork()

  const [fieldModal, setFieldModal] = useState<Partial<CustomFieldSchema>>(null)

  const [highlightedField, setHighlightedField] = useState('')
  const { mutateAsync: addField, isLoading: isAdding } =
    useAddMemberSchemaField()
  const { mutateAsync: updateField, isLoading: isUpdating } =
    useUpdateMemberSchemaField()
  const { mutateAsync: unArchiveField, isLoading: isUnarchiving } =
    useUnArchiveMemberSchemaField()
  const { mutateAsync: reorderFields } = useReorderMemberSchemaFields()
  const { completeStep } = useAdminOnboarding()

  const [currentFields, setCurrentFields] = useState(
    fields.filter(f => !f.archived),
  )
  const archivedFields = fields.filter(f => f.archived)

  const availableFields = suggestedFields.filter(
    aField => fields.findIndex(f => f.key === aField.key) === -1,
  )

  useEffect(() => {
    setCurrentFields(fields.filter(f => !f.archived))
  }, [fields])

  const onDragEnd = result => {
    if (!result.destination) {
      return
    }

    const newCurrentFields = SortableList.reorder(
      currentFields,
      result.source.index,
      result.destination.index,
    )
    setCurrentFields(newCurrentFields)

    const newItems = [...newCurrentFields, ...archivedFields]
    const input = newItems.map(({ key }, index) => ({
      index,
      key,
    }))
    reorderFields({
      input,
    })
  }

  return (
    <div className="grid grid-cols-12 h-screen">
      <div className="web:col-span-12 web:lg:col-span-4 bg-surface p-5 flex flex-col space-y-5 text-content-subdued max-h-screen overflow-auto">
        <BackgroundProvider backgroundType="surface">
          <div className="flex items-center space-s-2">
            <h4 className="text-lg font-medium flex-grow">
              <T defaultMessage="Member fields" id="Generics.MemberFields" />
            </h4>
            <Button
              variant="secondaryNeutral"
              leadingIcon={<SvgIcon name="plus" />}
              size="md"
              className="flex-shrink-0"
              onClick={() => {
                setFieldModal({})
              }}
            >
              <T defaultMessage="Add field" id="Generics.AddField" />
            </Button>
          </div>
          <div className="space-y-2">
            <SortableList
              id="member-fields"
              onDragEnd={onDragEnd}
              items={currentFields.map(it => ({ ...it, id: it.key }))}
              renderItem={field => (
                <SortableList.Item id={field.key}>
                  <div className="flex items-center space-s-2">
                    <FieldRow
                      dragHandle={<SortableList.Handle />}
                      field={field}
                      setHighlightedField={setHighlightedField}
                      setFieldModal={setFieldModal}
                    />
                  </div>
                </SortableList.Item>
              )}
            />
            {!currentFields?.length ? (
              <p className="mb-5">
                <T
                  defaultMessage="You have not added any profile fields."
                  id="Admin.MemberFields.NoFieldsAdded"
                />
              </p>
            ) : null}
            {archivedFields?.length ? (
              <Accordion inset="flush">
                <Accordion.Button>
                  <T
                    defaultMessage="Archived fields"
                    id="Generics.ArchivedFields"
                  />
                </Accordion.Button>
                <Accordion.Panel>
                  <List spacing="xs">
                    {fields
                      .filter(f => f.archived)
                      .map(field => {
                        return (
                          <List.Item key={field.key}>
                            <FieldRow
                              field={field}
                              setHighlightedField={setHighlightedField}
                              setFieldModal={setFieldModal}
                            />
                          </List.Item>
                        )
                      })}
                  </List>
                </Accordion.Panel>
              </Accordion>
            ) : null}
          </div>
          <h4>
            <T
              defaultMessage="Suggested fields"
              id="Generics.SuggestedFields"
            />
          </h4>
          <List spacing="xs">
            {availableFields?.length ? (
              availableFields.map(field => {
                return (
                  <List.Item key={field.key}>
                    <button
                      type="button"
                      onClick={async () => {
                        const confirmed = await confirm({
                          title: $t(
                            {
                              defaultMessage:
                                'Are you sure you want to add {field_name} ({field_key}) field?',
                              id: 'Admin.MemberFields.Add.Confirmation',
                            },
                            {
                              field_name: field.name,
                              field_key: field.key,
                            },
                          ),
                          description: $t({
                            defaultMessage:
                              'Suggested fields come with predefined validation, UI, and values. You will be able to archive them in the future.',
                            id: 'Admin.MemberFields.Add.Description',
                          }),
                          proceedLabel: $t({
                            defaultMessage: 'Add field',
                            id: 'Generics.AddField',
                          }),
                          cancelLabel: $t({
                            defaultMessage: 'Cancel',
                            id: 'Generics.Cancel',
                          }),
                        })

                        if (!confirmed) {
                          return
                        }

                        addField({ input: field })
                        completeStep({
                          step: 'profile-fields',
                        })
                      }}
                      className="w-full group border border-line-subdued rounded-base p-2 px-3 mb-2 bg-surface hover:bg-surface-hovered flex space-s-2 items-center"
                    >
                      <Icon className="w-5 h-5 flex-shrink-0">
                        {getFieldIcon(field)}
                      </Icon>
                      <span className="flex-grow text-start">{field.name}</span>
                      <SvgIcon
                        className="opacity-0 group-hover:opacity-100 w-4 h-4"
                        name="plus"
                      />
                    </button>
                  </List.Item>
                )
              })
            ) : (
              <div>
                <T
                  defaultMessage="You’ve already added all the available fields."
                  id="Admin.MemberFields.NoMoreToAdd"
                />
              </div>
            )}
          </List>
        </BackgroundProvider>
      </div>
      <div className="hidden lg:flex col-span-8 p-5 flex-col space-y-5 max-h-screen overflow-auto">
        <div className="flex-shrink-0">
          <MemberHeader memberId={member?.id} attached="none" />
        </div>
        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-8 flex flex-col space-y-5">
            <PostCardListLoading pulse={false} count={3} />
          </div>
          <div className="col-span-4">
            <MemberAbout
              preview
              highlightedField={highlightedField}
              memberId={member?.id}
              memberFields={fields}
            />
          </div>
        </div>
      </div>
      {!!fieldModal && (
        <FieldModal
          field={fieldModal}
          open
          onClose={() => setFieldModal(null)}
          onAddField={addField}
          onUpdateField={updateField}
          onUnArchiveField={unArchiveField}
          isAdding={isAdding}
          isUpdating={isUpdating}
          isUnarchiving={isUnarchiving}
          targetType="member"
        />
      )}
    </div>
  )
}
